import React, { useContext, useEffect, useRef, useState } from 'react';
import useSearchParamsWrapper from '../../hooks/useSearchParamsWrapper';
import { SOIL_TYPE_CODES } from '../../hooks/useSoilTypes';
import { PERFORMANCE_INDEX_LAYERS } from '../Field/constants/performance-index-layers';
import { FieldSearchWrap } from '../Fields/fields.styles';
import { PanelLeft } from '../Shared/PanelLeft/PanelLeft';
import Filters from './Filters';
import PolicyMakersMap from './PolicyMakersMap';
import api from '../../api/api';
import Proj from 'proj4';
import { parseFullYearFromScope } from '../../utils/utils';
import { ButtonPrimaryMedium } from '../Shared/Button/Button';
import { polygon } from '@turf/helpers';
import area from '@turf/area';
import { t } from 'ttag';
import { SignalMarkerVisualization } from '@area-monitoring/react-components';
import useAreaMonitoringDimensions from '../../hooks/useAreaMonitoringDimensions';
import styled, { useTheme } from 'styled-components';
import { Fields, FieldPanelRight } from './PolicyMakers.styles';
import { LanguageContext } from '../../contexts/LanguageContext';
import Spinner from '../../media/spinner.svg';
import { CROP_TYPE_CODES } from '../../hooks/useCropTypes';
import useFilters from '../Shared/Filter/useFilters';
import { FILTERS } from '../Field/constants/filters';
import { HARVEST_DATE_CODES, SEED_DATE_CODES, COVER_CROP_CODES } from '../Field/constants/filters';
import { PRE_CROP_CODES } from '../../hooks/usePreCropTypes';

const SignalChartWrap = styled.div`
  width: 100%;
  height: 100%;
  flex-grow: 1;
  flex-shrink: 0;
`;

function calculateMean(array) {
  return array.reduce((a, b) => a + b) / array.length;
}

function calculateStandardDeviation(array) {
  const mean = calculateMean(array);
  return Math.sqrt(calculateMean(array.map((x) => Math.pow(x - mean, 2))));
}

export default function PolicyMakers() {
  const theme = useTheme();
  const [language] = useContext(LanguageContext);

  const NUMBER_OF_FIELD_LISTS_TO_COMPARE = 2;
  const NUMBER_OF_FIELDS_PER_LIST = 100;
  const SIGNAL_TYPE = 'S2L1C_NDVI';

  const searchParams = Array(NUMBER_OF_FIELD_LISTS_TO_COMPARE).fill(useSearchParamsWrapper());

  const { handleFilterChange, handleFilterToggleClick, handleFilterCloseClick, activeFilter, savedFilters } =
    useFilters(getFilters());

  useEffect(() => {
    onSavedFiltersUpdate(savedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilters]);

  function getFilters() {
    const { cropType, year, mapLayer, soilType, seedDate, harvestDate, coverCrop, preCrop } =
      searchParams[0].getSearchParams();
    return {
      cropType,
      year,
      mapLayer,
      soilType,
      seedDate,
      harvestDate,
      coverCrop,
      preCrop,
    };
  }

  const signalRef = useRef();
  const [fieldsList, setFieldList] = useState([]);
  const [savedFiltersList, setSavedFiltersList] = useState([]);
  const [totalAreaOfFieldsList, setTotalAreaOfFieldsList] = useState([]);
  const performanceIndex = PERFORMANCE_INDEX_LAYERS[0].id;

  const signalChartRef = useRef();
  const { width: signalMarkerWidth, height: signalMarkerHeight } =
    useAreaMonitoringDimensions(signalChartRef);

  const [signalTypes, setSignalTypes] = useState(null);
  const [customSignalsData, setCustomSignalsData] = useState(null);

  const [fetchSignalsInProgress, setFetchSignalsInProgress] = useState(false);

  useEffect(() => {
    if (savedFiltersList[0] && savedFiltersList[0].cropType !== 0) {
      initialiseFieldsList(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFiltersList[0]]);

  useEffect(() => {
    if (savedFiltersList[1] && savedFiltersList[1].cropType !== 0) {
      initialiseFieldsList(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFiltersList[1]]);

  const [bbox, setBBox] = useState(() => {
    const { _southWestLat, _southWestLng, _northEastLat, _northEastLng } = searchParams[0].getSearchParams();
    return {
      bounds: {
        _northEast: {
          lng: parseFloat(_northEastLng),
          lat: parseFloat(_northEastLat),
        },
        _southWest: {
          lng: parseFloat(_southWestLng),
          lat: parseFloat(_southWestLat),
        },
      },
      wasUserDrag: false,
    };
  });

  function onSavedFiltersUpdate(index, savedFilters) {
    const newSavedFiltersList = savedFiltersList.slice();
    newSavedFiltersList[index] = savedFilters;
    setSavedFiltersList(newSavedFiltersList);
  }

  function createAreaMonitoringBBoxFromLeafletBBox(leafletBBox) {
    const bbox3857 = Object.keys(leafletBBox).map((bound) => {
      const lngLat = [leafletBBox[bound].lng, leafletBBox[bound].lat];
      return Proj('EPSG:4326', 'EPSG:3857', lngLat);
    });

    const areaMonitoringBBox = [bbox3857[1][0], bbox3857[1][1], bbox3857[0][0], bbox3857[0][1]];
    return areaMonitoringBBox.join(',');
  }

  async function initialiseFieldsList(index) {
    if (signalRef.current) {
      signalRef.current.abort();
      setFetchSignalsInProgress(false);
    }

    const clearedFieldsList = fieldsList.slice();
    clearedFieldsList[index] = [];
    setFieldList(clearedFieldsList);
    setCustomSignalsData(null);

    signalRef.current = new AbortController();
    try {
      const bbox4326 = bbox.bounds;
      const bbox3857 = createAreaMonitoringBBoxFromLeafletBBox(bbox4326);

      const fetchedFields = await getFields(savedFiltersList[index], bbox3857);
      const newFieldsList = fieldsList.slice();
      newFieldsList[index] = fetchedFields;
      setFieldList(newFieldsList);

      const newTotalAreaOfFieldsList = totalAreaOfFieldsList.slice();
      newTotalAreaOfFieldsList[index] = calculateTotalAreaOfFields(fetchedFields);
      setTotalAreaOfFieldsList(newTotalAreaOfFieldsList);
    } catch (error) {
      console.warn(error);
    }
  }

  function calculateTotalAreaOfFields(fields) {
    const squareMetersToHectares = 0.0001;
    const areaSqareMeters = fields.reduce((prev, field) => {
      return prev + area(polygon(field.geometry.coordinates[0]));
    }, 0);

    return areaSqareMeters * squareMetersToHectares;
  }

  async function updateSignals() {
    const signalsList = arrayOfObjects(fieldsList.length);
    const requests = [];

    setFetchSignalsInProgress(true);

    try {
      fieldsList.forEach((fields, index) => {
        fields.forEach((field) => {
          const timespan = Object.values(getTimespan(savedFiltersList[index].year)).join('/');
          requests.push(
            api()
              .fetchSignals(field.id, timespan, SIGNAL_TYPE, signalRef.current.signal)
              .then((response) => {
                injectSignalsData(signalsList, response.data, index);
              }),
          );
        });
      });

      await Promise.all(requests);
    } catch (error) {
      return [];
    }

    const meanSignalsList = calculateMeanSignals(signalsList);

    prepareSignalTypes();
    prepareCustomSignalsData(meanSignalsList);
    setFetchSignalsInProgress(false);
  }

  function prepareSignalTypes() {
    const signalTypes = [];

    arrayOfIndices(NUMBER_OF_FIELD_LISTS_TO_COMPARE).forEach((index) => {
      signalTypes.push({
        signalTypeId: `CUSTOM_DATA.CUSTOM_SIGNAL_${index + 1}`,
        shown: 'true',
        foiId: `Fields-${index + 1}`,
        lineColor: index === 0 ? theme.fieldColor : theme.selectedNeighbouringFieldColor,
      });
    });

    setSignalTypes(signalTypes);
  }

  function prepareCustomSignalsData(meanSignalsList) {
    const customSignalsData = {
      signalTypes: [],
      signals: [],
    };

    arrayOfIndices(NUMBER_OF_FIELD_LISTS_TO_COMPARE).forEach((index) => {
      customSignalsData.signalTypes.push({
        id: `CUSTOM_DATA.CUSTOM_SIGNAL_${index + 1}`,
        signalClass: 'NB',
        code: `CUSTOM_SIGNAL_${index + 1}`,
        names: {
          en: `Field ${index + 1}`,
          de: `Felder ${index + 1}`,
        },
      });
    });

    meanSignalsList.forEach((meanSignals, index) => {
      const values = [];
      Object.keys(meanSignals)
        .sort()
        .forEach((key) => {
          values.push({
            time: index === 0 ? key : normaliseYear(key, savedFiltersList[index].year),
            mean: meanSignals[key].mean,
            stDev: meanSignals[key].stDev,
          });
        });

      customSignalsData.signals.push({
        id: `CUSTOM_DATA.CUSTOM_SIGNAL_${index + 1}`,
        code: `CUSTOM_SIGNAL_${index + 1}`,
        values: values,
      });
    });

    setCustomSignalsData(customSignalsData);
  }

  // Signal Chart Component only supports visualisation from the same scope.
  // That is why we normalise any subsequent years to the first year.
  function normaliseYear(date, yearCode) {
    if (!savedFiltersList[0]) {
      return date;
    }

    const newYear = parseFullYearFromScope(savedFiltersList[0].year);
    const oldYear = parseFullYearFromScope(yearCode);

    return date.replace(oldYear, newYear);
  }

  function injectSignalsData(signalsList, data, index) {
    const values = data.signals[0].values;

    values.forEach((value) => {
      if (signalsList[index][value.time]) {
        signalsList[index][value.time].push(value.mean);
      } else {
        signalsList[index][value.time] = [value.mean];
      }
    });
  }

  function calculateMeanSignals(signalsList) {
    const meanSignalsList = arrayOfObjects(fieldsList.length);

    signalsList.forEach((signals, index) => {
      Object.keys(signalsList[index]).forEach((key) => {
        meanSignalsList[index][key] = {
          mean: calculateMean(signalsList[index][key]),
          stDev: calculateStandardDeviation(signalsList[index][key]),
        };
      });
    });

    return meanSignalsList;
  }

  function getTimespan(scope) {
    const year = parseFullYearFromScope(scope);
    const from = `${year}-01-01`;
    const to = `${year}-12-31`;
    return { from, to };
  }

  async function getFields(savedFilters, bbox) {
    try {
      const { cropType, year, soilType, seedDate, harvestDate, coverCrop, preCrop } = savedFilters;
      let cropId = `${year}.CL_CROP.${cropType}`;

      const parsedBbox = bbox.split(',').map((coord) => parseFloat(coord));
      const center = [(parsedBbox[0] + parsedBbox[2]) / 2, (parsedBbox[1] + parsedBbox[3]) / 2];
      const centerString = center.join(' ');

      let filter = [];
      if (cropType !== CROP_TYPE_CODES.ALL) {
        filter.push(`cropId="${cropId}"`);
      }
      if (soilType !== SOIL_TYPE_CODES.ALL) {
        filter.push(`soilTypeId="${year}.CL_ST.${soilType}"`);
      }
      if (seedDate !== SEED_DATE_CODES.ALL) {
        filter.push(`attributes.att_seeddate_class="${seedDate}"`);
      }
      if (harvestDate !== HARVEST_DATE_CODES.ALL) {
        filter.push(`attributes.att_harvdate_class="${harvestDate}"`);
      }
      if (coverCrop !== COVER_CROP_CODES.ALL) {
        filter.push(`attributes.att_covercrop="${coverCrop}"`);
      }
      if (preCrop !== PRE_CROP_CODES.ALL) {
        filter.push(`attributes.att_precrop="${preCrop}"`);
      }

      const queryArguments = [
        `filter=${filter.join(' and ')}`,
        `size=${NUMBER_OF_FIELDS_PER_LIST}`,
        `sort=distance(geometry)`,
        `fromGeometry="SRID=3857;Point(${centerString})"`,
      ];

      const { signal } = signalRef.current;

      return await api().fetchFields(year, queryArguments, signal);
    } catch {
      return [];
    }
  }

  function renderSignalChart() {
    const savedFilter = savedFiltersList[0];
    const scope = savedFilter?.year;

    return (
      <SignalChartWrap className="signal-marker-wrap" ref={(el) => (signalChartRef.current = el)}>
        {signalChartRef.current && signalTypes && customSignalsData && scope && !fetchSignalsInProgress && (
          <SignalMarkerVisualization
            width={signalMarkerWidth}
            height={signalMarkerHeight}
            scope={scope}
            timespan={getTimespan(scope)}
            signalTypes={signalTypes}
            customSignalsData={customSignalsData}
            language={language}
          />
        )}
      </SignalChartWrap>
    );
  }

  function arrayOfIndices(length) {
    return Array.from({ length }, (_, index) => index);
  }

  function arrayOfObjects(length) {
    return Array.from({ length }, () => ({}));
  }

  return (
    <FieldSearchWrap>
      <PanelLeft>
        {arrayOfIndices(NUMBER_OF_FIELD_LISTS_TO_COMPARE).map((index) => (
          <Fields key={index}>
            <h3>
              <span
                className="square"
                style={{ backgroundColor: index === 0 ? theme.fieldColor_0 : theme.fieldColor_1 }}
              ></span>
              {t`Fields`} {index + 1}
            </h3>
            <Filters
              getSearchParams={searchParams[index].getSearchParams}
              onSavedFiltersUpdate={(savedFilters) => onSavedFiltersUpdate(index, savedFilters)}
            ></Filters>
            {totalAreaOfFieldsList[index] ? (
              <div className="total-area">
                {t`Total area`}: {totalAreaOfFieldsList[index].toPrecision(3)} {t`ha`}
              </div>
            ) : null}
          </Fields>
        ))}
        <ButtonPrimaryMedium
          onClick={() => {
            updateSignals();
          }}
          className="field-panel-button"
        >
          {t`Get Signals`}
        </ButtonPrimaryMedium>
        {fetchSignalsInProgress ? (
          <div className="spinner-wrap float-right">
            <img src={Spinner} alt="" />
          </div>
        ) : null}
      </PanelLeft>
      <FieldPanelRight>
        <div className="field-panel-top-right">
          <PolicyMakersMap
            scope0={savedFiltersList[0]?.year}
            scope1={savedFiltersList[1]?.year}
            bbox={bbox.bounds}
            wasUserDrag={bbox.wasUserDrag}
            fieldsList={fieldsList}
            activeLayer={performanceIndex}
            onMoveEnd={(bbox) => {
              setBBox({ bounds: bbox, wasUserDrag: true });
            }}
            activeFilter={activeFilter}
            mapLayer={savedFilters.mapLayer}
            onFilterCloseClick={() => handleFilterCloseClick(FILTERS.mapLayer)}
            onOutsideFilterClick={() => handleFilterCloseClick(FILTERS.mapLayer)}
            onToggleFilterClick={() => handleFilterToggleClick(FILTERS.mapLayer)}
            onFilterChangeClick={(mapLayer) => handleFilterChange(FILTERS.mapLayer, mapLayer, true)}
          />
        </div>
        <div className="field-panel-bottom-right">
          <div className="field-panel-chart">{renderSignalChart()}</div>
        </div>
      </FieldPanelRight>
    </FieldSearchWrap>
  );
}
